import React from "react";
import { Box, Checkbox, Collapse } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Field, useForm, useFormState } from "react-final-form";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import RffTextareaField from "../../../components/finalForm/inputs/RffTextareaField";
import colors from "../../../theme/colors";
import { APP_TOKENS } from "../../../components/publicForm/publicFormTokens";
import { EXPERIENCE_CATEGORIES } from "../applicationFormShared";

/**
 * Prior investment experience: pick the categories that apply, then fill counts
 * for those categories only.
 *
 * Deliberately not an AG Grid, despite the repo default of "every data table is
 * AG Grid" — this isn't a data table. It's a cluster of paired numeric inputs
 * whose visibility depends on a checkbox above them, and a grid can't express
 * that: it would show all six rows at all times, including ground-up rows to a
 * fix-and-flip applicant.
 *
 * The written payload is the flat one the backend expects: each row writes
 * values[row.name] = { less_than_three_years, greater_than_three_years }.
 *
 * `hideSummary` drops the free-text summary, for the review step — which edits
 * the categories and the summary as two separate rows and would otherwise show
 * the textarea twice.
 */
const ExperienceSection = ({ hideSummary = false }) => (
  <Flx column gap={5}>
    <Flx column gap={2}>
      <Flx column gap={0.5}>
        <Txt
          component="div"
          sx={{ fontWeight: 600, fontSize: 14, color: APP_TOKENS.ink }}
        >
          Which of these have you done before?
        </Txt>
        <Txt component="div" sx={{ fontSize: 13, color: APP_TOKENS.inkMuted }}>
          Select all that apply. Fill in the experience counts that appear, or
          write a detailed summary below — either one works.
        </Txt>
      </Flx>

      <Flx column gap={1.5}>
        {EXPERIENCE_CATEGORIES.map((category) => (
          <ExperienceCategory key={category.flag} category={category} />
        ))}
      </Flx>
    </Flx>

    {hideSummary ? null : (
      <Flx column gap={1}>
        <Txt
          component="div"
          sx={{ fontWeight: 600, fontSize: 14, color: APP_TOKENS.ink }}
        >
          Tell us about it
        </Txt>
        <RffTextareaField
          suppressGrid
          name="borrowerExperienceSummary"
          label="Summary of borrower property investor experience"
          placeholder="Describe the projects you've taken on — scope, timelines, and outcomes are all useful."
        />
      </Flx>
    )}
  </Flx>
);

/** One checkbox, with its own count rows revealed directly underneath it. */
const ExperienceCategory = ({ category }) => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const checked = values?.[category.flag] === true;

  const handleToggle = (next) => {
    form.change(category.flag, next ? true : undefined);
    // Clear this category's counts on deselect so a category the applicant
    // backed out of can't leave stale numbers in the payload.
    if (!next) {
      category.rows.forEach((row) => form.change(row.name, undefined));
    }
  };

  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${checked ? theme.palette.primary.main : APP_TOKENS.border}`,
        background: checked
          ? alpha(theme.palette.primary.main, 0.06)
          : APP_TOKENS.surface,
        borderRadius: "10px",
        overflow: "hidden",
        transition: "border-color 160ms ease, background 160ms ease",
      })}
    >
      <Box
        component="label"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1.25,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <Checkbox
          size="small"
          checked={checked}
          onChange={(e) => handleToggle(e.target.checked)}
          sx={{ p: 0.5 }}
        />
        {/* Matches CheckboxRow exactly — these sit alongside the Housing Events
            and Credit & Legal checklists and should read as the same control.
            No weight bump on check either: the row already signals selection
            with its border and tint, so bolding the label double-signals it. */}
        <Txt
          component="div"
          sx={{
            fontSize: 14,
            lineHeight: 1.45,
            color: colors.slate700,
          }}
        >
          {category.label}
        </Txt>
      </Box>

      <Collapse in={checked} unmountOnExit>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <Box
            sx={{
              background: APP_TOKENS.surface,
              border: `1px solid ${APP_TOKENS.border}`,
              borderRadius: "8px",
              p: 1.5,
            }}
          >
            <Flx column gap={1.25}>
              {/* Column captions live here, once, so they aren't repeated over
                  every row's inputs. */}
              <Flx centerY gap={1.5}>
                <Txt
                  component="div"
                  sx={{ flex: 1, fontSize: 12, color: APP_TOKENS.inkMuted }}
                >
                  How many have you completed?
                </Txt>
                <Flx gap={1} sx={{ flexShrink: 0 }}>
                  <ColumnCaption>Under 3 yrs</ColumnCaption>
                  <ColumnCaption>Over 3 yrs</ColumnCaption>
                </Flx>
              </Flx>

              {category.rows.map((row) => (
                <CountRow key={row.name} row={row} />
              ))}
            </Flx>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

// One source of truth for the column width so the captions above stay lined up
// with the inputs below them.
const COUNT_COL_WIDTH = { xs: 76, sm: 92 };

const ColumnCaption = ({ children }) => (
  <Txt
    component="div"
    sx={{
      width: COUNT_COL_WIDTH,
      flexShrink: 0,
      fontSize: 10.5,
      textAlign: "center",
      color: APP_TOKENS.inkMuted,
    }}
  >
    {children}
  </Txt>
);

/**
 * A single experience row: label plus the two horizon counts. Writes the nested
 * `{ less_than_three_years, greater_than_three_years }` object the backend reads.
 *
 * Stays a row at every breakpoint — the captions are rendered once as headers
 * above, so stacking on mobile would orphan them from their inputs.
 */
const CountRow = ({ row }) => (
  <Flx centerY gap={1.5}>
    <Txt
      component="div"
      sx={{ flex: 1, minWidth: 0, fontSize: 13, color: APP_TOKENS.ink }}
    >
      {row.label}
    </Txt>
    <Flx gap={1} sx={{ flexShrink: 0 }}>
      <CountInput name={`${row.name}.less_than_three_years`} />
      <CountInput name={`${row.name}.greater_than_three_years`} />
    </Flx>
  </Flx>
);

const CountInput = ({ name }) => (
  <Field
    name={name}
    // Numbers, not the strings a bare text input would store — the counts feed
    // experience underwriting downstream.
    parse={(v) => {
      if (v === "" || v === null || v === undefined) return undefined;
      const n = Number(v);
      return Number.isNaN(n) ? undefined : n;
    }}
    render={({ input }) => (
      <Box
        component="input"
        type="number"
        min={0}
        inputMode="numeric"
        placeholder="0"
        {...input}
        value={input.value ?? ""}
        sx={{
          width: COUNT_COL_WIDTH,
          flexShrink: 0,
          textAlign: "center",
          fontSize: 14,
          fontFamily: "inherit",
          color: APP_TOKENS.ink,
          border: `1px solid ${APP_TOKENS.border}`,
          borderRadius: "6px",
          py: 0.75,
          px: 1,
          outline: "none",
          "&:focus": {
            borderColor: colors.action,
            boxShadow: `0 0 0 3px ${colors.blue100}`,
          },
        }}
      />
    )}
  />
);

export default ExperienceSection;
